import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { usersAPI, welcomeQuestionsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import {
  UserPlus,
  UserX,
  UserCheck,
  Edit2,
  Circle,
  Sparkles,
  Headset,
  KeyRound,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChangePasswordModal } from "@/components/ChangePassword";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const roleLabels = {
  attendant: "Atendente",
  analyst: "Analista",
  admin: "Administrador",
};

const Attendants = () => {
  const [attendants, setAttendants] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newAttendant, setNewAttendant] = useState({
    name: "",
    email: "",
    password: "",
    role: "attendant",
  });

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAttendant, setEditingAttendant] = useState({
    id: "",
    name: "",
    role: "attendant",
    departments: [],
  });

  const { user, register } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    const interval = setInterval(loadOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await loadAttendants();
    await loadOnlineStatus();
    await loadDepartments();
  };

  const loadDepartments = async () => {
    try {
      const res = await welcomeQuestionsAPI.getAll();
      const formattedDepts = res.data.map((q, idx) => {
        const optionNum = idx + 1;
        const optionText = q.question;
        if (
          optionText.trim().startsWith(`${optionNum} `) ||
          optionText.trim().startsWith(`${optionNum}-`) ||
          optionText.trim().startsWith(`${optionNum}.`)
        ) {
          return optionText;
        }
        return `${optionNum} - ${optionText}`;
      });
      setDepartments(formattedDepts);
    } catch (error) {
      console.error("Error loading departments:", error);
    }
  };

  const loadAttendants = async () => {
    try {
      const includeInactive = user?.role === "admin";
      const response = await usersAPI.getAll({ active_only: !includeInactive });
      setAttendants(response.data.filter((attendant) => includeInactive || attendant.is_active !== false));
    } catch (error) {
      console.error("Error loading attendants:", error);
    }
  };

  const loadOnlineStatus = async () => {
    try {
      const response = await usersAPI.getOnline();
      setOnlineUsers(response.data.map((u) => u.user_id));
    } catch (error) {
      console.error("Error loading online status:", error);
    }
  };

  const handleAddAttendant = async () => {
    try {
      await register(
        newAttendant.name,
        newAttendant.email,
        newAttendant.password,
        newAttendant.role,
      );
      toast({
        title: "Atendente cadastrado",
        description:
          "Novo atendente adicionado com sucesso. Edite-o para atribuir os departamentos.",
      });
      setShowAddDialog(false);
      setNewAttendant({ name: "", email: "", password: "", role: "attendant" });
      loadAttendants();
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error.response?.data?.detail || "Erro ao cadastrar atendente.",
        variant: "destructive",
      });
    }
  };

  const handleChangeActiveStatus = async (id, isActive) => {
    setUpdatingStatusId(id);
    try {
      await usersAPI.setActive(id, isActive);
      setAttendants((previous) =>
        previous.map((attendant) =>
          attendant.id === id ? { ...attendant, is_active: isActive } : attendant,
        ),
      );
      if (!isActive) {
        setOnlineUsers((previous) => previous.filter((userId) => userId !== id));
      }
      toast({
        title: isActive ? "Atendente reativado" : "Atendente inativado",
        description: isActive
          ? "O acesso ao sistema foi liberado novamente."
          : "O acesso ao sistema foi bloqueado. O cadastro e o histórico foram preservados.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error.response?.data?.detail || "Erro ao alterar o status do atendente.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleEditAttendant = async () => {
    try {
      await usersAPI.update(editingAttendant.id, {
        name: editingAttendant.name,
        role: editingAttendant.role,
        departments: editingAttendant.departments,
      });
      toast({
        title: "Atendente atualizado",
        description: "Os dados do atendente foram alterados com sucesso.",
      });
      setShowEditDialog(false);
      loadAttendants();
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error.response?.data?.detail || "Erro ao atualizar atendente.",
        variant: "destructive",
      });
    }
  };

  const handleToggleAITriage = async (attendantId, currentValue) => {
    try {
      await usersAPI.update(attendantId, {
        ai_triage_enabled: !currentValue,
      });

      setAttendants(
        attendants.map((a) =>
          a.id === attendantId ? { ...a, ai_triage_enabled: !currentValue } : a,
        ),
      );

      toast({
        title: !currentValue
          ? "Triagem por IA ativada"
          : "Triagem por IA desativada",
        description: !currentValue
          ? "Você agora receberá avaliações da IA antes do atendimento."
          : "Triagem por IA desativada.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao atualizar configuração de IA.",
        variant: "destructive",
      });
    }
  };

  const openEditModal = (attendant) => {
    setEditingAttendant({
      id: attendant.id,
      name: attendant.name,
      role: attendant.role || "attendant",
      departments: attendant.departments || [],
    });
    setShowEditDialog(true);
  };

  const toggleDepartment = (dept) => {
    setEditingAttendant((prev) => {
      const isSelected = prev.departments.includes(dept);
      if (isSelected) {
        return {
          ...prev,
          departments: prev.departments.filter((d) => d !== dept),
        };
      } else {
        return { ...prev, departments: [...prev.departments, dept] };
      }
    });
  };

  return (
    <div className="space-y-6" data-testid="attendants-page">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between">
        <div className="flex flex-col">
          <h1 className="text-2xl sm:text-3xl font-bold flex gap-2 text-gray-800">
            <Headset className="w-8 h-8 text-indigo-600 shrink-0" />
            Equipe
          </h1>
          <p className="text-gray-600 mt-1">
            {user?.role === "admin"
              ? "Gerencie os membros da equipe e suas configurações"
              : "Visualize os membros da equipe e seus dados"}
          </p>
        </div>

        {user?.role === "admin" && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="w-4/5 sm:w-auto self-center gap-2 shadow-sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Adicionar Membro
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl">
                  Cadastrar Novo Membro
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2 w-full">
                <div className="space-y-2">
                  <Label className="font-bold text-gray-700">Nome</Label>
                  <Input
                    value={newAttendant.name}
                    onChange={(e) =>
                      setNewAttendant({ ...newAttendant, name: e.target.value })
                    }
                    className="w-full bg-white"
                    placeholder="Ex: João da Silva"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold text-gray-700">Email</Label>
                  <Input
                    type="email"
                    value={newAttendant.email}
                    onChange={(e) =>
                      setNewAttendant({
                        ...newAttendant,
                        email: e.target.value,
                      })
                    }
                    className="w-full bg-white"
                    placeholder="joao@empresa.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold text-gray-700">Senha</Label>
                  <Input
                    type="password"
                    value={newAttendant.password}
                    onChange={(e) =>
                      setNewAttendant({
                        ...newAttendant,
                        password: e.target.value,
                      })
                    }
                    className="w-full bg-white"
                    placeholder="••••••••"
                  />
                </div>

                {/* Botões com Responsividade Ouro */}
                <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddDialog(false)}
                    className="w-full sm:w-auto"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAddAttendant}
                    className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 shadow-md"
                  >
                    Cadastrar Membro
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {attendants.map((attendant) => {
              const isActive = attendant.is_active !== false;
              const isOnline = isActive && onlineUsers.includes(attendant.id);
              const aiEnabled = attendant.ai_triage_enabled || false;
              const isAdmin = user?.role === "admin";
              const isSelf = user?.id === attendant.id;
              const canEdit = isAdmin;
              const canChangeStatus = isAdmin && !isSelf;
              const canToggleAI = isActive && (isAdmin || isSelf);

              return (
                <div
                  key={attendant.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg border border-transparent hover:border-blue-100 transition-colors gap-4"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-lg">
                          {(attendant.name || "U").charAt(0).toUpperCase()}
                        </span>
                      </div>

                      {isOnline && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-800">
                          {attendant.name}{" "}
                          {isSelf && (
                            <span className="text-gray-400 font-normal text-sm">
                              (Você)
                            </span>
                          )}
                        </p>
                        {isOnline && (
                          <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">
                            Online
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                            isActive ? "text-green-700 bg-green-50" : "text-gray-600 bg-gray-200"
                          }`}
                        >
                          {isActive ? "Ativo" : "Inativo"}
                        </span>
                        {aiEnabled && isActive && (
                          <span className="flex items-center gap-1 text-[10px] font-bold text-purple-600 uppercase tracking-wider bg-purple-50 px-2 py-0.5 rounded">
                            <Sparkles className="w-3 h-3" />
                            IA
                          </span>
                        )}
                      </div>

                      <p
                        className="text-sm text-gray-600 truncate"
                        title={attendant.email}
                      >
                        {attendant.email}
                      </p>

                      <p className="text-xs text-gray-500 mt-1">
                        {roleLabels[attendant.role] || "Atendente"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end gap-2 sm:gap-4 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-gray-200 sm:pl-4">
                    {canToggleAI && (
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            Triagem por IA
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {aiEnabled ? "Ativada" : "Desativada"}
                          </p>
                        </div>
                        <Switch
                          checked={aiEnabled}
                          onCheckedChange={() =>
                            handleToggleAITriage(attendant.id, aiEnabled)
                          }
                        />
                      </div>
                    )}

                    <div className="flex justify-center sm:justify-end items-center space-x-2 mt-2 sm:mt-0 w-full sm:w-auto">
                      {isSelf && (
                        <ChangePasswordModal>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-2"
                          >
                            <KeyRound className="w-4 h-4 text-purple-600" />
                            <span className="hidden sm:inline text-purple-700">
                              Alterar Senha
                            </span>
                          </Button>
                        </ChangePasswordModal>
                      )}
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(attendant)}
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                      )}

                      {canChangeStatus && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={updatingStatusId !== null}
                              aria-label={`${isActive ? "Inativar" : "Reativar"} ${attendant.name}`}
                              title={isActive ? "Inativar atendente" : "Reativar atendente"}
                              className={isActive
                                ? "gap-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                : "gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"}
                            >
                              {isActive
                                ? <UserX className="w-4 h-4" />
                                : <UserCheck className="w-4 h-4" />}
                              {isActive ? "Inativar" : "Reativar"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="w-full max-w-[95vw] sm:max-w-md rounded-2xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {isActive ? "Inativar atendente?" : "Reativar atendente?"}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {isActive
                                  ? "O acesso deste atendente será bloqueado, inclusive nas sessões já abertas. O cadastro, os atendimentos e o histórico continuarão salvos. Você poderá reativá-lo depois."
                                  : "O atendente poderá entrar novamente com o mesmo email e senha. O cadastro e o histórico serão mantidos."}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
                              <AlertDialogCancel className="sm:mt-0">
                                Cancelar
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  handleChangeActiveStatus(attendant.id, !isActive)
                                }
                                disabled={updatingStatusId !== null}
                                className={isActive
                                  ? "bg-red-600 hover:bg-red-700 text-white"
                                  : "bg-green-600 hover:bg-green-700 text-white"}
                              >
                                {isActive ? "Sim, inativar" : "Sim, reativar"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {attendants.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                Nenhum atendente cadastrado
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">
              Editar Atendente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 w-full">
            <div className="space-y-2">
              <Label className="font-bold text-gray-700">Nome Completo</Label>
              <Input
                value={editingAttendant.name}
                onChange={(e) =>
                  setEditingAttendant({
                    ...editingAttendant,
                    name: e.target.value,
                  })
                }
                placeholder="Novo nome"
                className="w-full bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-bold text-gray-700">Cargo</Label>
              <Select
                value={editingAttendant.role}
                onValueChange={(role) =>
                  setEditingAttendant({ ...editingAttendant, role })
                }
              >
                <SelectTrigger className="w-full bg-white" data-testid="user-role-select">
                  <SelectValue placeholder="Selecione o cargo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attendant">Atendente</SelectItem>
                  <SelectItem value="analyst">Analista</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Analistas acompanham toda a operação, sem acesso às configurações administrativas.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-bold text-gray-700">
                Departamentos (Filtro de Fila)
              </Label>
              <div className="space-y-1 mt-1 border border-gray-200 rounded-md p-2 bg-gray-50 max-h-48 overflow-y-auto overflow-x-hidden w-full shadow-inner">
                {departments.map((dept) => (
                  <label
                    key={dept}
                    className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white hover:shadow-sm transition-all rounded w-full"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 shrink-0"
                      checked={editingAttendant.departments.includes(dept)}
                      onChange={() => toggleDepartment(dept)}
                    />
                    {/* A magia do texto inquebrável para setores com nome gigante */}
                    <span className="text-sm font-medium text-gray-700 truncate flex-1 min-w-0">
                      {dept}
                    </span>
                  </label>
                ))}
                {departments.length === 0 && (
                  <p className="text-xs text-gray-500 italic text-center py-4">
                    Nenhuma pergunta de boas-vindas cadastrada.
                  </p>
                )}
              </div>
            </div>

            {/* Botões com Responsividade Ouro */}
            <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEditAttendant}
                className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 shadow-md"
              >
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendants;
